import type { FlagKind, ScoreGroup } from "@/lib/enums";
import {
  BAND_LEGEND,
  bandColorVar,
  bandSurfaceVar,
  bandTextVar,
  deltaToneVar,
  formatDelta,
  formatScore,
  ROW_STATUS_LABELS,
  type RowStatus,
  scoreBand,
  scoreBarWidth,
  statusColorVars,
} from "@/lib/plan-audit-format";

/**
 * The small, repeated pieces of the audit vocabulary: a score, a band, a run
 * status, a movement figure.
 *
 * Every colour comes from `plan-audit-format`, which returns token names — so no
 * component here hard-codes a brand or status colour, and a re-brand in
 * design-system/tokens.css reaches all of them.
 */

/**
 * A score in its band colour. `null` renders an em dash, never a zero.
 *
 * `row` is the list treatment for the headline score — 17px in the display
 * face, per the design. `muted` drops the band colour for a mono grey figure:
 * a first-pass score is context for the latest one, so colouring both makes the
 * row read as two competing verdicts.
 */
export function ScoreValue({
  score,
  size = "md",
  muted = false,
}: {
  score: number | null;
  size?: "sm" | "md" | "lg" | "row";
  muted?: boolean;
}) {
  const band = scoreBand(score);
  const classes = {
    sm: "font-semibold text-[13px]",
    md: "font-semibold text-base",
    lg: "font-display font-semibold text-3xl tracking-tight",
    row: "font-display font-bold text-[17px] tracking-tight",
  } as const;
  return (
    <span
      className={`tabular-nums ${muted ? "text-[13px]" : classes[size]}`}
      style={
        muted
          ? { color: "var(--fg-3)", fontFamily: "var(--font-mono)" }
          : { color: bandColorVar(band) }
      }
    >
      {formatScore(score)}
    </span>
  );
}

/** The 0–10 bar beside a section's score. Absent sections show an empty track. */
export function ScoreBar({ score }: { score: number | null }) {
  const band = scoreBand(score);
  return (
    <div
      className="pa-track"
      role="img"
      aria-label={score === null ? "No score recorded" : `${score} out of 10`}
    >
      <span style={{ width: scoreBarWidth(score), background: bandColorVar(band) }} />
    </div>
  );
}

/** The rubric legend: what each band means the reader should do. */
export function BandLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {BAND_LEGEND.map(({ band, label }) => (
        <span key={band} className="flex items-center gap-1.5 text-[11px] text-[var(--fg-3)]">
          <span
            className="size-2 rounded-full"
            style={{ background: bandColorVar(band) }}
            aria-hidden="true"
          />
          {label}
        </span>
      ))}
    </div>
  );
}

export function StatusPill({ status }: { status: RowStatus }) {
  const { surface, text, dot } = statusColorVars(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
      style={{ background: surface, color: text }}
    >
      <span className="size-1.5 rounded-full" style={{ background: dot }} aria-hidden="true" />
      {ROW_STATUS_LABELS[status]}
    </span>
  );
}

/**
 * A hallucination and a gap are different findings — one asserts something the
 * intake never said, the other drops something it did — so they get different
 * colours rather than one generic "flag" treatment.
 */
export function FlagKindPill({ kind }: { kind: FlagKind }) {
  const gap = kind === "gap";
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase"
      style={{
        background: gap
          ? "color-mix(in srgb, var(--brand-g-purple) 12%, white)"
          : "var(--warning-bg)",
        color: gap ? "var(--brand-g-purple)" : "var(--warning-fg)",
      }}
    >
      {gap ? "Gap" : "Hallucination"}
    </span>
  );
}

/**
 * Movement, signed and coloured by whether it is good news. `upIsGood` exists
 * because for scores up is better and for flags-per-plan down is — the number
 * cannot know which, so the caller says.
 */
export function DeltaValue({
  delta,
  upIsGood = true,
  className = "",
}: {
  delta: number | null;
  upIsGood?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`font-semibold tabular-nums ${className}`}
      style={{ color: deltaToneVar(delta, upIsGood) }}
    >
      {formatDelta(delta)}
    </span>
  );
}

/** A KPI tile: label, big value, and a note that explains the movement. */
export function MetricTile({
  label,
  value,
  note,
  noteTone,
}: {
  label: string;
  value: string;
  note?: string;
  noteTone?: string;
}) {
  return (
    <div className="pa-card">
      <div className="pa-eyebrow">{label}</div>
      <div className="font-display mt-1.5 text-[32px] font-bold tracking-tight text-[var(--brand-primary)] tabular-nums">
        {value}
      </div>
      {note !== undefined && (
        <div className="mt-1 text-xs font-semibold" style={{ color: noteTone ?? "var(--fg-3)" }}>
          {note}
        </div>
      )}
    </div>
  );
}

/** The group a section sits under, tinted by that group's mean score. */
export function GroupHeading({
  group,
  mean,
  sectionCount,
}: {
  group: ScoreGroup;
  mean: number | null;
  sectionCount: number;
}) {
  const band = scoreBand(mean);
  return (
    <div className="mb-1 flex items-center justify-between gap-3 border-b border-[var(--border-1)] pb-2">
      <h3 className="font-display text-sm font-bold text-[var(--brand-primary)]">{group}</h3>
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-[var(--fg-3)]">
          {sectionCount} section{sectionCount === 1 ? "" : "s"}
        </span>
        <span
          className="rounded-md px-2 py-0.5 text-xs font-semibold tabular-nums"
          style={{ background: bandSurfaceVar(band), color: bandTextVar(band) }}
        >
          {formatScore(mean)}
        </span>
      </div>
    </div>
  );
}

/**
 * A horizontal bar in a list — used for group averages and weak sections.
 * `tone` overrides the band colour where the value is a count rather than a score.
 */
export function BarRow({
  label,
  value,
  fraction,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  /** 0–1. Clamped, so a bad input cannot draw outside the track. */
  fraction: number;
  tone?: string;
  onClick?: () => void;
}) {
  const width = `${Math.max(0, Math.min(1, fraction)) * 100}%`;
  const body = (
    <>
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <span className="truncate text-[13px] text-[var(--fg-1)]">{label}</span>
        <span className="shrink-0 text-xs font-semibold text-[var(--fg-2)] tabular-nums">
          {value}
        </span>
      </div>
      <div className="pa-track">
        <span style={{ width, background: tone ?? "var(--brand-accent)" }} />
      </div>
    </>
  );

  if (onClick === undefined) return <div className="py-2">{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full cursor-pointer border-0 bg-transparent py-2 text-left font-[inherit]"
    >
      {body}
    </button>
  );
}

/** "2/4 fields" and similar meta, in one consistent treatment. */
export function MetaText({ children }: { children: React.ReactNode }) {
  return <span className="text-[11px] text-[var(--fg-3)]">{children}</span>;
}
